import React, { useCallback, useEffect, useState, useRef } from 'react';
import debounce from 'lodash/debounce';

// Images
import GiphyAttributionImg from 'Shared/images/PoweredBy_200px-White_HorizText.png';

// API
import { searchGiphy } from './api/giphy.api';

// Components
import WpTextField from 'Wpds/components/text-field/WpTextField';
import GifPickerLoading from './loading/GifPickerloading';
import GifList from './list/GifList';

// Styles
import './WpGifPicker.scss';

const initialValueOptions = ['awesome work', 'great job', 'high five', 'success', 'celebrate'];

export type WpGifPickerProps = {
  onSelect?: Function;
  autoFocus?: boolean;
  initialSearchTerm?: string;
};

const WpGifPicker = ({ onSelect, autoFocus = true, initialSearchTerm }: WpGifPickerProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [searchString, setSearchString] = useState('');
  const [gifs, setGifs] = useState(null);

  const randomValue = () => initialValueOptions[(initialValueOptions.length * Math.random()) | 0];

  const searchForGifs = value => {
    // random search string to set initial group of gifs if no search value supplied
    const toSearch = value ? value : randomValue();
    searchGiphy({ searchValue: toSearch }).then(response => {
      setIsLoading(true);
      if (response.status === 200) {
        setGifs(
          response.data.data.map(({ id, images }) => {
            return { id, images };
          })
        );
        setIsLoading(false);
      }
    });
  };

  const debouneSearch = useRef(
    debounce(
      value => {
        searchForGifs(value);
      },
      300,
      { leading: false, trailing: true }
    )
  );

  const handleSelectGif = useCallback(gif => {
    if (onSelect) onSelect(gif);
  }, []);

  const handleUpdateSearchString = useCallback(value => {
    setSearchString(value);
    debouneSearch.current(value);
  }, []);

  const handleClearSearchInput = () => {
    handleUpdateSearchString('');
  };

  useEffect(() => {
    searchForGifs(initialSearchTerm || randomValue());
  }, []);

  const noGifs = !isLoading && gifs && gifs.length === 0;
  const showGifs = !isLoading && gifs && gifs.length > 0;
  const baseClassName = 'WpGifPicker';

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__search-bar`}>
        <WpTextField
          id="gif-picker-search-field"
          prefixIcon="search"
          label="Search Gifs"
          placeholder="Search GIFs"
          labelHidden
          hideBorder
          onChange={handleUpdateSearchString}
          onClear={searchString && handleClearSearchInput}
          value={searchString}
          autoFocus={autoFocus}
          max={50}
        />
      </div>
      <div className={`${baseClassName}__gifs`}>
        {isLoading && <GifPickerLoading />}
        {showGifs && <GifList gifs={gifs} onSelect={handleSelectGif} />}
        {noGifs && <>no gifs match</>}
      </div>
      <div className={`${baseClassName}__footer`}>
        <img src={GiphyAttributionImg} className={`${baseClassName}__footer__img`} />
      </div>
    </div>
  );
};

export default WpGifPicker;
